import { posApi } from "@/api/pos-api";
import { reservationSendRequestsApi } from "@/api/reservation-send-requests";
import type { AdminTab } from "@/components/pos/pos-layout";
import { ZoneTableEditor } from "@/components/pos/zone-table-editor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import useTableFilters from "@/hooks/table/useTableFilters";
import type {
  ActivityLog,
  AdminMetrics,
  Category,
  Customer,
  Order,
  Payment,
  Product,
  ReservationSendRequest,
  Shift,
  StationKey,
  StationPrinterConfig,
  StationTicket,
  Table,
  User,
  Zone,
} from "@/lib/pos-types";
import { cn } from "@/lib/utils";
import { isApiEnabled } from "@/routes/role-routing";
import { usePayments } from "@/services/payment/queries";
import { ActivitiesPrivate } from "@/views/private/ActivitiesPrivate";
import { ClientsPrivate } from "@/views/private/clients/ClientsPrivate";
import { PaymentsPrivate } from "@/views/private/PaymentsPrivate";
import { ProductsPrivate } from "@/views/private/ProductsPrivate";
import { UsersPrivate } from "@/views/private/UsersPrivate";
import {
  Activity,
  AlertCircle,
  ChefHat,
  Clock,
  DollarSign,
  Image as ImageIcon,
  Pencil,
  Plus,
  Printer,
  Table2,
  Trash2,
  Users,
  Utensils,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { toast } from "sonner";
import { InventoryPrivate } from "@/views/private/InventoryPrivate";
import { ModalVRecipe, ModalVRecipeRef } from "@/components/ui/modals/ModalVRecipe";


type ProductUpsertPayload = {
  name: string;
  description?: string;
  price: number;
  categoryId: string;
  stationKey?: StationKey | null;
  imageFile?: File | null;
  availabilityRules?: { days: number[]; start: string; end: string }[];
  priceRules?: { days: number[]; start: string; end: string; price: number }[];
};

// ─────────────────────────────────────────
// Props
// ─────────────────────────────────────────
interface AdminViewProps {
  activityLogs: ActivityLog[];
  categories: Category[];
  currentShift?: Shift | null;
  currentUser: User;
  products: Product[];
  stationPrinters?: StationPrinterConfig[];
  tables: Table[];
  orders?: Order[];
  users: User[];
  customers?: Customer[];
  zones: string[];
  zoneItems?: Zone[];
  onTablesChange?: (tables: Table[]) => void;
  onProductsChange?: (products: Product[]) => void;
  onTableUpdate?: (
    tableId: string,
    patch: Partial<Table>,
  ) => void | Promise<void>;
  onTableCreate?: (payload: {
    number: number;
    capacity: number;
    zone: string;
    layoutGx?: number;
    layoutGy?: number;
    layoutSize?: number;
    layoutShape?: string;
  }) => Promise<void>;
  onTableDelete?: (tableId: string) => Promise<void>;
  onZoneCreate?: (payload: {
    name: string;
    order: number;
    layoutWidth?: number;
    layoutHeight?: number;
  }) => Promise<void>;
  onZoneUpdate?: (
    zoneId: string,
    patch: {
      name?: string;
      order?: number;
      layoutWidth?: number;
      layoutHeight?: number;
    },
  ) => Promise<void>;
  onZoneDelete?: (zoneId: string) => Promise<void>;
  onProductUpdate?: (
    productId: string,
    patch: Record<string, unknown> & {
      available?: boolean;
      imageFile?: File | null;
    },
  ) => void | Promise<void>;
  onProductCreate?: (payload: ProductUpsertPayload) => Promise<void>;
  onProductDelete?: (productId: string) => Promise<void>;
  onCategoryCreate?: (payload: {
    name: string;
    sortOrder?: number;
    stationKey?: StationKey | null;
  }) => Promise<Category>;
  onCategoryUpdate?: (
    categoryId: string,
    payload: {
      name?: string;
      sortOrder?: number;
      stationKey?: StationKey | null;
    },
  ) => Promise<void>;
  onCategoryDelete?: (categoryId: string) => Promise<void>;
  onStationPrinterUpdate?: (
    stationKey: StationKey,
    patch: Partial<StationPrinterConfig>,
  ) => void | Promise<void>;
  onStationPrinterTestPrint?: (
    stationKey: StationKey,
  ) => Promise<{ success: boolean; errno: number; error: string }>;
  onUserCreate?: (payload: {
    name: string;
    email: string;
    password: string;
    role: User["role"];
  }) => Promise<void>;
  onUserUpdate?: (
    userId: string,
    payload: {
      name?: string;
      email?: string;
      password?: string | null;
      role?: User["role"];
    },
  ) => Promise<void>;
  onUserDelete?: (userId: string) => Promise<void>;
  onCustomerCreate?: (payload: {
    type: Customer["type"];
    name: string;
    taxId?: string | null;
    email?: string | null;
    phone?: string | null;
    discountPercent?: number;
    creditEnabled?: boolean;
    creditLimit?: number;
  }) => Promise<void>;
  onCustomerUpdate?: (
    customerId: string,
    payload: {
      type?: Customer["type"];
      name?: string;
      taxId?: string | null;
      email?: string | null;
      phone?: string | null;
      discountPercent?: number;
      creditEnabled?: boolean;
      creditLimit?: number;
    },
  ) => Promise<void>;
  onCustomerDelete?: (customerId: string) => Promise<void>;
  activeTab?: AdminTab;
  onTabChange?: (tab: AdminTab) => void;
}

// ─────────────────────────────────────────
// Component
// ─────────────────────────────────────────
export function AdminView({
  activityLogs,
  categories,
  currentUser,
  products,
  stationPrinters = [],
  tables,
  orders = [],
  customers = [],
  zones,
  zoneItems = [],
  onTableUpdate,
  onTableCreate,
  onTableDelete,
  onZoneCreate,
  onZoneUpdate,
  onZoneDelete,
  onProductUpdate,
  onProductCreate,
  onProductDelete,
  onCategoryCreate,
  onCategoryUpdate,
  onCategoryDelete,
  onStationPrinterUpdate,
  onStationPrinterTestPrint,
  activeTab: activeTabProp = "dashboard",
  onTabChange,
}: AdminViewProps) {
  const isReadOnly = currentUser?.role === "auditor";
  const CATEGORY_ALL = "__all__";
  const CATEGORY_UNCATEGORIZED = "__uncategorized__";

  const activeTab = activeTabProp;
  const setActiveTab = (tab: AdminTab) => onTabChange?.(tab);
  const [productsData, setProductsData] = useState<Product[]>(() => products);
  const [selectedCategory, setSelectedCategory] = useState<string>(
    () => categories[0]?.id ?? CATEGORY_ALL,
  );
  const [stationPrintersData, setStationPrintersData] = useState<
    StationPrinterConfig[]
  >(() => stationPrinters);
  const [customersData, setCustomersData] = useState<Customer[]>(
    () => customers,
  );

  const modalVRecipeRef = useRef<ModalVRecipeRef>(null);


  useEffect(() => {
    setProductsData(products);
  }, [products]);
  useEffect(() => {
    if (categories.length === 0) return;
    if (selectedCategory === CATEGORY_ALL) return;
    if (selectedCategory === CATEGORY_UNCATEGORIZED) return;
    const exists = categories.some((c) => c.id === selectedCategory);
    if (!exists) setSelectedCategory(categories[0]?.id ?? CATEGORY_ALL);
  }, [categories, selectedCategory]);
  useEffect(() => {
    if (stationPrintersData.length === 0 && stationPrinters.length > 0) {
      setStationPrintersData(stationPrinters);
    }
  }, [stationPrinters, stationPrintersData.length]);
  useEffect(() => {
    setCustomersData(customers);
  }, [customers]);

  // ─── Modal States ───
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryName, setCategoryName] = useState("");
  const [categoryStationKey, setCategoryStationKey] = useState<StationKey | "">(
    "",
  );

  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productName, setProductName] = useState("");
  const [productPrice, setProductPrice] = useState("");
  const [productDesc, setProductDesc] = useState("");
  const [productCategoryId, setProductCategoryId] = useState("");
  const [productImage, setProductImage] = useState<File | null>(null);
  const [productImagePreview, setProductImagePreview] = useState("");
  const [availabilityRules, setAvailabilityRules] = useState<
    { days: number[]; start: string; end: string }[]
  >([]);
  const [priceRules, setPriceRules] = useState<
    { days: number[]; start: string; end: string; price: number }[]
  >([]);
  const [productStationKey, setProductStationKey] = useState<StationKey | "">(
    "",
  );

  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [customerType, setCustomerType] = useState<Customer["type"]>("person");
  const [customerName, setCustomerName] = useState("");
  const [customerTaxId, setCustomerTaxId] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerDiscount, setCustomerDiscount] = useState("0");
  const [customerCreditEnabled, setCustomerCreditEnabled] = useState(false);
  const [customerCreditLimit, setCustomerCreditLimit] = useState("0");

  const { tableParams, tableKey, resetFilters, handleTableChange } =
    useTableFilters({
      initialFilters: true
        ? {
            created_at: ["today"],
          }
        : {},
    });

  const { data, isLoading, isError, error, isFetching, refetch } =
    usePayments(tableParams);

  const [cashierPayments, setCashierPayments] = useState<Payment[]>([]);
  const [cashierLoading, setCashierLoading] = useState(false);
  const [cashierError, setCashierError] = useState<string | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const [reservationSendRequests, setReservationSendRequests] = useState<
    ReservationSendRequest[]
  >([]);
  const [reservationSendRequestsLoading, setReservationSendRequestsLoading] =
    useState(false);
  const [failedStationTickets, setFailedStationTickets] = useState<
    StationTicket[]
  >([]);
  const [failedStationTicketsLoading, setFailedStationTicketsLoading] =
    useState(false);
  const [failedStationTicketsStation, setFailedStationTicketsStation] =
    useState<StationKey | "all">("all");
  const [adminMetricsData, setAdminMetricsData] = useState<AdminMetrics | null>(null);
  const [adminMetricsLoading, setAdminMetricsLoading] = useState(false);

  const localDishesReady = useMemo(() => {
    if (!orders || orders.length === 0) return { list: [], total: 0 };

    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);

    const map: Record<string, number> = {};

    orders.forEach((order) => {
      (order.items || []).forEach((item) => {
        if (item.status === "cancelled") return;
        const isReadyOrDelivered =
          item.status === "ready" || item.status === "delivered";

        const itemReadyTime = item.readyAt
          ? new Date(item.readyAt)
          : item.addedAt
          ? new Date(item.addedAt)
          : null;

        const isToday = itemReadyTime
          ? itemReadyTime >= startOfDay
          : isReadyOrDelivered;

        if (isReadyOrDelivered && isToday) {
          const name = item.productName || "Sin nombre";
          map[name] = (map[name] || 0) + (item.quantity || 1);
        }
      });
    });

    const list = Object.entries(map)
      .map(([name, quantity]) => ({ name, quantity }))
      .sort((a, b) => b.quantity - a.quantity);

    const total = list.reduce((sum, d) => sum + d.quantity, 0);

    return { list, total };
  }, [orders]);

  const effectiveDishesList = useMemo(() => {
    if (
      adminMetricsData?.dishesReadyList &&
      adminMetricsData.dishesReadyList.length > 0
    ) {
      return adminMetricsData.dishesReadyList;
    }
    return localDishesReady.list;
  }, [adminMetricsData, localDishesReady.list]);

  const effectiveDishesTotal = useMemo(() => {
    if (
      typeof adminMetricsData?.dishesReadyTotalCount === "number" &&
      adminMetricsData.dishesReadyTotalCount > 0
    ) {
      return adminMetricsData.dishesReadyTotalCount;
    }
    if (
      adminMetricsData?.dishesReadyList &&
      adminMetricsData.dishesReadyList.length > 0
    ) {
      return adminMetricsData.dishesReadyList.reduce(
        (acc, d) => acc + d.quantity,
        0,
      );
    }
    return localDishesReady.total;
  }, [adminMetricsData, localDishesReady.total]);

  const openNewCategory = () => {
    if (isReadOnly) return;
    setEditingCategory(null);
    setCategoryName("");
    setCategoryStationKey("");
    setIsCategoryModalOpen(true);
  };

  const openEditCategory = (cat: Category) => {
    if (isReadOnly) return;
    setEditingCategory(cat);
    setCategoryName(cat.name);
    setCategoryStationKey((cat.stationKey as StationKey | undefined) ?? "");
    setIsCategoryModalOpen(true);
  };

  const openNewProduct = () => {
    if (isReadOnly) return;
    setEditingProduct(null);
    setProductName("");
    setProductPrice("");
    setProductDesc("");
    const initialCategory =
      selectedCategory === CATEGORY_ALL ||
      selectedCategory === CATEGORY_UNCATEGORIZED
        ? CATEGORY_UNCATEGORIZED
        : selectedCategory || categories[0]?.id || CATEGORY_UNCATEGORIZED;
    setProductCategoryId(initialCategory);
    setProductImage(null);
    setProductImagePreview("");
    setAvailabilityRules([]);
    setPriceRules([]);
    setProductStationKey("");
    setIsProductModalOpen(true);
  };

  const openEditProduct = (prod: Product) => {
    if (isReadOnly) return;
    setEditingProduct(prod);
    setProductName(prod.name);
    setProductPrice(prod.price.toString());
    setProductDesc(prod.description || "");
    const catExists = categories.some((c) => c.id === prod.categoryId);
    setProductCategoryId(catExists ? prod.categoryId : CATEGORY_UNCATEGORIZED);
    setProductImage(null);
    const baseUrl =
      import.meta.env.VITE_API_BASE_URL?.replace("/api", "") || "";
    setProductImagePreview(
      prod.image ? `${baseUrl}/storage/${prod.image}` : "",
    );
    setAvailabilityRules(prod.availabilityRules || []);
    setPriceRules(
      (prod.priceRules || []).map((r) => ({ ...r, price: Number(r.price) })),
    );
    setProductStationKey((prod.stationKey as StationKey | undefined) ?? "");
    setIsProductModalOpen(true);
  };

  /*   const openNewCustomer = () => {
      setEditingCustomer(null);
      setCustomerType("person");
      setCustomerName("");
      setCustomerTaxId("");
      setCustomerEmail("");
      setCustomerPhone("");
      setCustomerDiscount("0");
      setCustomerCreditEnabled(false);
      setCustomerCreditLimit("0");
      setIsCustomerModalOpen(true);
    };
  
    const openEditCustomer = (c: Customer) => {
      setEditingCustomer(c);
      setCustomerType(c.type);
      setCustomerName(c.name);
      setCustomerTaxId(c.taxId || "");
      setCustomerEmail(c.email || "");
      setCustomerPhone(c.phone || "");
      setCustomerDiscount(String(c.discountPercent || 0));
      setCustomerCreditEnabled(Boolean(c.creditEnabled));
      setCustomerCreditLimit(String(c.creditLimit || 0));
      setIsCustomerModalOpen(true);
    }; */

  const handleSaveCategory = async () => {
    if (isReadOnly || !categoryName.trim()) return;
    try {
      if (editingCategory) {
        await onCategoryUpdate?.(editingCategory.id, {
          name: categoryName.trim(),
          stationKey: categoryStationKey || null,
        });
        toast.success("Categoría actualizada");
      } else {
        await onCategoryCreate?.({
          name: categoryName.trim(),
          sortOrder: categories.length,
          stationKey: categoryStationKey || null,
        });
        toast.success("Categoría creada");
      }
      setIsCategoryModalOpen(false);
    } catch {
      toast.error("Error al guardar categoría");
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (
      isReadOnly ||
      !confirm(
        "¿Seguro de eliminar esta categoría? Los productos podrían quedar huérfanos",
      )
    )
      return;
    try {
      await onCategoryDelete?.(id);
      setIsCategoryModalOpen(false);
      if (selectedCategory === id)
        setSelectedCategory(categories[0]?.id ?? CATEGORY_ALL);
      toast.success("Categoría eliminada");
    } catch {
      toast.error("Error al eliminar categoría");
    }
  };

  const handleSaveProduct = async () => {
    if (isReadOnly || !productName.trim() || !productPrice || !productCategoryId) return;
    try {
      let categoryIdToUse = productCategoryId;
      if (categoryIdToUse === CATEGORY_UNCATEGORIZED) {
        const normalize = (s: string) =>
          s
            .trim()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");
        const existing = categories.find(
          (c) => normalize(c.name) === "sin categoria",
        );
        if (existing) {
          categoryIdToUse = existing.id;
        } else if (onCategoryCreate) {
          const created = await onCategoryCreate({
            name: "Sin categoría",
            sortOrder: categories.length + 999,
            stationKey: undefined,
          });
          categoryIdToUse = created.id;
        } else {
          toast.error("No se puede crear producto sin categoría");
          return;
        }
      }
      const payload: ProductUpsertPayload = {
        name: productName.trim(),
        price: Number(productPrice),
        categoryId: categoryIdToUse,
        description: productDesc.trim(),
        availabilityRules,
        priceRules,
      };
      if (productImage) payload.imageFile = productImage;
      payload.stationKey = productStationKey
        ? (productStationKey as StationKey)
        : null;

      if (editingProduct) {
        await onProductUpdate?.(editingProduct.id, payload);
        toast.success("Producto actualizado");
      } else {
        await onProductCreate?.(payload);
        toast.success("Producto creado");
      }
      setIsProductModalOpen(false);
    } catch {
      toast.error("Error al guardar producto");
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (isReadOnly || !confirm("¿Seguro de eliminar este producto?")) return;
    try {
      await onProductDelete?.(id);
      setIsProductModalOpen(false);
      toast.success("Producto eliminado");
    } catch {
      toast.error("Error al eliminar producto");
    }
  };

  /*  const deleteCustomer = async (c: Customer) => {
     if (!onCustomerDelete) return;
     const ok = window.confirm(`Eliminar cliente "${c.name}"?`);
     if (!ok) return;
     try {
       await onCustomerDelete(c.id);
       toast.success("Cliente eliminado");
     } catch (err) {
       toast.error("No se pudo eliminar cliente", {
         description: getApiErrorMessage(err),
       });
     }
   }; */

  const refreshReservationSendRequests = useCallback(async () => {
    if (!isApiEnabled()) return;
    setReservationSendRequestsLoading(true);
    try {
      const list = await reservationSendRequestsApi.list({
        status: "pending",
        limit: 200,
      });
      setReservationSendRequests(list);
    } catch (err) {
      toast.error("No se pudo cargar solicitudes", {
        description: getApiErrorMessage(err),
      });
    } finally {
      setReservationSendRequestsLoading(false);
    }
  }, []);

  const refreshAdminMetrics = useCallback(async () => {
    if (!isApiEnabled()) return;
    setAdminMetricsLoading(true);
    try {
      const metrics = await posApi.getAdminMetrics({
        station:
          failedStationTicketsStation === "all"
            ? undefined
            : failedStationTicketsStation,
        sinceHours: 24,
        limit: 30,
      });
      setAdminMetricsData(metrics);
      setFailedStationTickets(metrics.failedTickets);
    } catch {
      // Ignore background errors
    } finally {
      setAdminMetricsLoading(false);
    }
  }, [failedStationTicketsStation]);

  const refreshFailedStationTickets = useCallback(async () => {
    await refreshAdminMetrics();
  }, [refreshAdminMetrics]);

  useEffect(() => {
    if (activeTab !== "dashboard") return;
    void refreshReservationSendRequests();
    void refreshAdminMetrics();
    const timer = setInterval(() => {
      void refreshReservationSendRequests();
      void refreshAdminMetrics();
    }, 10_000);
    return () => clearInterval(timer);
  }, [activeTab, refreshReservationSendRequests, refreshAdminMetrics]);

  const approveReservationSendRequest = useCallback(
    async (req: ReservationSendRequest) => {
      if (!isApiEnabled()) return;
      try {
        await reservationSendRequestsApi.approve(req.id);
        toast.success("Envío aprobado", {
          description: `Mesa ${req.tableNumber}`,
        });
        void refreshReservationSendRequests();
      } catch (err) {
        toast.error("No se pudo aprobar", {
          description: getApiErrorMessage(err),
        });
      }
    },
    [refreshReservationSendRequests],
  );

  const rejectReservationSendRequest = useCallback(
    async (req: ReservationSendRequest) => {
      if (!isApiEnabled()) return;
      try {
        await reservationSendRequestsApi.reject(req.id);
        toast.success("Envío rechazado", {
          description: `Mesa ${req.tableNumber}`,
        });
        void refreshReservationSendRequests();
      } catch (err) {
        toast.error("No se pudo rechazar", {
          description: getApiErrorMessage(err),
        });
      }
    },
    [refreshReservationSendRequests],
  );

  const retryFailedStationTicket = useCallback(
    async (ticket: StationTicket) => {
      if (!isApiEnabled()) return;
      try {
        const res = await posApi.reprintStationTicket(ticket.id);
        if (res.printed) {
          toast.success("Reimpresión enviada", {
            description: `Mesa ${ticket.tableNumber}`,
          });
        } else {
          toast.error("No se pudo imprimir", {
            description: ticket.printError ?? "Error al imprimir",
          });
        }
        void refreshFailedStationTickets();
      } catch (err) {
        toast.error("No se pudo reimprimir", {
          description: getApiErrorMessage(err),
        });
      }
    },
    [refreshFailedStationTickets],
  );

  // ─── Helpers ───
  const formatPrice = (p: number) => `$${p.toLocaleString("es-CL")}`;
  const parseDigitsToInt = (s: string): number => {
    const digits = String(s ?? "").replace(/[^\d]/g, "");
    if (digits.length === 0) return 0;
    const n = Number.parseInt(digits, 10);
    return Number.isFinite(n) ? n : 0;
  };
  const formatThousands = (n: number) => n.toLocaleString("es-CL");
  const formatInputThousands = (s: string) =>
    formatThousands(parseDigitsToInt(s));
  const formatTime = (d: Date) =>
    new Date(d).toLocaleTimeString("es-CL", {
      hour: "2-digit",
      minute: "2-digit",
    });

  const getActivityColor = (a: string) => {
    if (a.includes("order")) return "bg-blue-500/10 text-blue-500";
    if (a.includes("payment")) return "bg-emerald-500/10 text-emerald-500";
    if (a.includes("cancel")) return "bg-red-500/10 text-red-500";
    return "bg-muted text-muted-foreground";
  };

  const getActivityIcon = (a: string) => {
    if (a.includes("order")) return ChefHat;
    if (a.includes("payment")) return DollarSign;
    return Activity;
  };

  const getRoleLabelEs = (role: User["role"]) => {
    if (role === "waiter") return "Mesero";
    if (role === "kitchen") return "Cocina";
    if (role === "cashier") return "Cajero";
    return "Admin";
  };

  const getActivityLabelEs = (action: ActivityLog["action"]) => {
    switch (action) {
      case "transferencia_de_mesa":
        return "Transferencia de mesa";
      case "table_opened":
        return "Mesa abierta";
      case "table_closed":
        return "Mesa cerrada";
      case "table_blocked":
        return "Mesa bloqueada";
      case "table_unblocked":
        return "Mesa desbloqueada";
      case "order_sent":
        return "Pedido enviado a cocina";
      case "order_ready":
        return "Pedido listo";
      case "order_adjusted":
        return "Comanda ajustada (Admin)";
      case "order_delivered":
        return "Pedido entregado";
      case "item_delivered":
        return "Producto entregado";
      case "payment_requested":
        return "Cobro solicitado";
      case "payment_received":
        return "Pago recibido";
      case "receipt_reprinted":
        return "Comprobante reimpreso";
      case "ticket_print_failed":
        return "Error al imprimir ticket";
      case "product_unavailable":
        return "Producto no disponible";
      case "discount_applied":
        return "Descuento aplicado";
      default:
        return String(action).replace(/_/g, " ");
    }
  };

  const formatActivityDetails = (log: ActivityLog) => {
    if (
      log.action === "payment_received" &&
      log.tableNumber &&
      !/mesa\s+\d+/i.test(log.details)
    ) {
      return `Mesa ${log.tableNumber} · ${log.details}`;
    }
    return log.details;
  };

  const toggleProductAvailability = (productId: string) => {
    if (isReadOnly) return;
    const product = productsData.find((p) => p.id === productId);
    if (product) {
      setProductsData((prev) =>
        prev.map((p) =>
          p.id === productId ? { ...p, available: !p.available } : p,
        ),
      );
      onProductUpdate?.(productId, { available: !product.available });
      toast.success("Disponibilidad actualizada");
    }
  };

  const stationList: { key: StationKey; label: string }[] = [
    { key: "cocina", label: "Cocina" },
    { key: "bar", label: "Bar" },
    { key: "retiro", label: "Retiro" },
    { key: "delivery", label: "Delivery" },
  ];

  const getPrinter = (stationKey: StationKey): StationPrinterConfig => {
    const found = stationPrintersData.find((p) => p.stationKey === stationKey);
    return (
      found ??
      ({
        id: "",
        stationKey,
        enabled: false,
        name: null,
        ip: null,
        port: 9100,
        driver: "raw_tcp",
        options: null,
        version: 0,
      } as StationPrinterConfig)
    );
  };

  const patchPrinterLocal = (
    stationKey: StationKey,
    patch: Partial<StationPrinterConfig>,
  ) => {
    setStationPrintersData((prev) => {
      const idx = prev.findIndex((p) => p.stationKey === stationKey);
      if (idx === -1) {
        return [...prev, { ...getPrinter(stationKey), ...patch }];
      }
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const savePrinter = async (stationKey: StationKey) => {
    const printer = getPrinter(stationKey);
    if (
      printer.enabled &&
      (!printer.ip || String(printer.ip).trim().length === 0)
    ) {
      toast.error("Falta IP", {
        description: "Define la IP para habilitar la impresora",
      });
      return;
    }
    try {
      await onStationPrinterUpdate?.(stationKey, {
        enabled: printer.enabled,
        name: printer.name,
        ip: printer.ip,
        port: printer.port,
        driver: printer.driver,
        options: printer.options,
      });
      toast.success("Impresora guardada");
    } catch {
      toast.error("Error al guardar impresora");
    }
  };

  const testPrintPrinter = async (stationKey: StationKey) => {
    const printer = getPrinter(stationKey);
    if (!printer.ip || String(printer.ip).trim().length === 0) {
      toast.error("Falta IP", {
        description: "Guarda una IP antes de imprimir prueba",
      });
      return;
    }
    try {
      if (!onStationPrinterTestPrint) throw new Error("disabled");
      const res = await onStationPrinterTestPrint(stationKey);
      console.log(res);
      if (res.success) {
        toast.success("Ticket de prueba creado correctamente");
      } else {
        toast.error("No se pudo crear el ticket");
      }
    } catch {
      toast.error("No se pudo imprimir");
    }
  };

  function getApiErrorMessage(err: unknown): string {
    const isRecord = (v: unknown): v is Record<string, unknown> =>
      Boolean(v) && typeof v === "object";
    const e = isRecord(err) ? err : {};
    const response = isRecord(e.response) ? e.response : null;
    const data = response && isRecord(response.data) ? response.data : null;
    const msg = data && typeof data.message === "string" ? data.message : null;
    if (msg && msg.trim().length > 0) return msg;
    const status =
      response && typeof response.status === "number" ? response.status : null;
    if (typeof status === "number") return `Error HTTP ${status}`;
    return "Error desconocido";
  }

  const getPrinterOptions = (printer: StationPrinterConfig) => {
    const raw =
      printer.options && typeof printer.options === "object"
        ? printer.options
        : {};
    const modeValue =
      raw && typeof (raw as Record<string, unknown>).mode === "string"
        ? String((raw as Record<string, unknown>).mode)
        : "text";
    const mode = modeValue === "escpos" ? "escpos" : "text";
    const paperValue =
      raw && typeof (raw as Record<string, unknown>).paper === "string"
        ? String((raw as Record<string, unknown>).paper)
        : "80";
    const paper = paperValue === "58" ? "58" : "80";
    const fontValue =
      raw && typeof (raw as Record<string, unknown>).font === "string"
        ? String((raw as Record<string, unknown>).font)
        : "A";
    const font = fontValue.toUpperCase() === "B" ? "B" : "A";
    const colsRaw = raw && (raw as Record<string, unknown>).cols;
    const colsNum = typeof colsRaw === "number" ? colsRaw : Number(colsRaw);
    const cols = Number.isFinite(colsNum)
      ? Math.max(16, Math.min(120, Math.floor(colsNum)))
      : 57;
    const cutRaw = raw && (raw as Record<string, unknown>).cut;
    const cut =
      typeof cutRaw === "boolean"
        ? cutRaw
        : typeof cutRaw === "string"
          ? ["true", "1", "yes", "y", "si", "sí"].includes(cutRaw.toLowerCase())
          : true;
    return { mode, paper, font, cols, cut };
  };

  const patchPrinterOptions = (
    stationKey: StationKey,
    patch: Record<string, unknown>,
  ) => {
    const current = getPrinter(stationKey);
    const currentOptions =
      current.options && typeof current.options === "object"
        ? current.options
        : {};
    patchPrinterLocal(stationKey, {
      options: { ...(currentOptions as Record<string, unknown>), ...patch },
    });
  };

  // ─── Stats ───
  const stats = useMemo(() => {
    const todayPayments = data?.items ?? [];

    console.log(todayPayments);

    const totalSales = todayPayments.reduce(
      (sum, p) => sum + Number(p.total || 0),
      0,
    );

    const paymentsToday = todayPayments.length;

    const tipsToday = todayPayments.reduce(
      (sum, p) => sum + Number(p.tip || 0),
      0,
    );

    const discountsToday = todayPayments.reduce(
      (sum, p) => sum + Number(p.discount || 0),
      0,
    );

    const logsToday = activityLogs.filter((l) => {
      const ts =
        l.timestamp instanceof Date ? l.timestamp : new Date(l.timestamp);

      const start = new Date();
      start.setHours(0, 0, 0, 0);

      const end = new Date(start);
      end.setDate(end.getDate() + 1);

      return ts >= start && ts < end;
    });

    const printFailuresToday = logsToday.filter(
      (l) => l.action === "ticket_print_failed",
    ).length;

    const tablesOccupied = tables.filter((t) => t.status === "occupied").length;

    const tablesPendingPayment = tables.filter(
      (t) => t.status === "pending-payment",
    ).length;

    const tablesReserved = tables.filter(
      (t) =>
        t.status === "blocked" &&
        (t.blockedReason ?? "").startsWith("Reservada"),
    ).length;

    const tablesBlockedOther = tables.filter(
      (t) =>
        t.status === "blocked" &&
        !(t.blockedReason ?? "").startsWith("Reservada"),
    ).length;

    const printersEnabled = stationPrintersData.filter((p) => p.enabled).length;

    return {
      totalSales,
      paymentsToday,
      avgTicket: paymentsToday > 0 ? totalSales / paymentsToday : 0,
      tipsToday,
      discountsToday,
      tablesOccupied,
      tablesPendingPayment,
      tablesReserved,
      tablesBlockedOther,
      printFailuresToday,
      printersEnabled,
      printersTotal: stationPrintersData.length,
      tablesTotal: tables.length,
    };
  }, [data, activityLogs, stationPrintersData, tables]);
  // ─── Tabs config ───
  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: Activity },
    { id: "salon", label: "Salón", icon: Table2 },
    { id: "inventory", label: "Inventario", icon: Utensils },
    { id: "menu", label: "Carta", icon: Utensils },
    { id: "printers", label: "Impresoras", icon: Printer },
    { id: "activity", label: "Actividad", icon: Clock },
    { id: "cashier", label: "Caja", icon: DollarSign },
    { id: "users", label: "Staff", icon: Users },
    { id: "clients", label: "Clientes", icon: DollarSign },
  ] as const;

  const categoryIdSet = useMemo(
    () => new Set(categories.map((c) => c.id)),
    [categories],
  );
  const uncategorizedCount = useMemo(
    () => productsData.filter((p) => !categoryIdSet.has(p.categoryId)).length,
    [productsData, categoryIdSet],
  );
  const filteredProducts = useMemo(() => {
    if (selectedCategory === CATEGORY_ALL) return productsData;
    if (selectedCategory === CATEGORY_UNCATEGORIZED)
      return productsData.filter((p) => !categoryIdSet.has(p.categoryId));
    return productsData.filter((p) => p.categoryId === selectedCategory);
  }, [productsData, selectedCategory, categoryIdSet]);

  return (
    <div className="flex flex-col h-full gap-4 sm:gap-6 p-4 sm:p-6 bg-background/50 ">
      {/* ── Mobile Tab Nav (hidden on desktop) ── */}
      <nav className="flex sm:hidden gap-1 p-1.5 bg-card/50 backdrop-blur-xl border border-border/50 rounded-2xl overflow-x-auto no-scrollbar shadow-sm shrink-0">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap",
              activeTab === tab.id
                ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
            )}
          >
            <tab.icon className="w-3.5 h-3.5 shrink-0" />
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>

      {/* ── Main content ── */}
      <div className="flex-1 min-w-0  flex flex-col gap-4 sm:gap-5">
        {/* ════ Dashboard ════ */}
        {activeTab === "dashboard" && (
          <div className="flex-1 overflow-y-auto no-scrollbar space-y-5">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
                Dashboard
              </h2>
              <Badge variant="outline" className="font-mono">
                {new Date().toLocaleDateString("es-CL")}
              </Badge>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                {
                  icon: <DollarSign className="w-4 h-4 text-emerald-500" />,
                  label: "Ventas hoy",
                  value: formatPrice(stats.totalSales),
                  sub: `${stats.paymentsToday} pagos`,
                  color: "text-emerald-500",
                  loading: isLoading,
                  fetching: isFetching && !isLoading,
                },
                {
                  icon: <Activity className="w-4 h-4 text-primary" />,
                  label: "Ticket prom.",
                  value: formatPrice(stats.avgTicket),
                  sub: "Por pago",
                  color: "text-primary",
                  loading: isLoading,
                  fetching: isFetching && !isLoading,
                },
                {
                  icon: <Table2 className="w-4 h-4 text-amber-500" />,
                  label: "Mesas ocupadas",
                  value: `${stats.tablesOccupied}`,
                  sub: `${stats.tablesPendingPayment} por cobrar · ${stats.tablesTotal} total`,
                  color: "text-amber-500",
                  loading: false,
                  fetching: false,
                },
                {
                  icon: <ChefHat className="w-4 h-4 text-orange-500" />,
                  label: "Platos listos",
                  value: `${effectiveDishesTotal}`,
                  sub: "Salidos hoy de cocina",
                  color: "text-orange-500",
                  loading: false,
                  fetching: false,
                },
              ].map((s) =>
                s.loading ? (
                  <Card
                    key={s.label}
                    className="bg-card/50 backdrop-blur-md border border-border/50 shadow-lg"
                  >
                    <CardHeader className="pb-1 px-4 pt-4">
                      <CardDescription className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
                        {s.icon} {s.label}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="px-4 pb-4 space-y-2 mt-1">
                      <Skeleton className="h-7 w-24 bg-muted-foreground/10" />
                      <Skeleton className="h-3 w-16 bg-muted-foreground/10" />
                    </CardContent>
                  </Card>
                ) : (
                  <Card
                    key={s.label}
                    className={cn(
                      "bg-card/50 backdrop-blur-md border-border/50 shadow-lg hover:border-primary/30 transition-all group relative",
                      s.fetching && "opacity-75"
                    )}
                  >
                    {s.fetching && (
                      <div className="absolute top-2.5 right-2.5 flex items-center justify-center">
                        <div className="w-3.5 h-3.5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                      </div>
                    )}
                    <CardHeader className="pb-1 px-4 pt-4">
                      <CardDescription className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
                        {s.icon} {s.label}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="px-4 pb-4">
                      <p className={cn("text-2xl font-black italic", s.color)}>
                        {s.value}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {s.sub}
                      </p>
                    </CardContent>
                  </Card>
                )
              )}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                {
                  icon: <DollarSign className="w-4 h-4 text-emerald-500" />,
                  label: "Propinas hoy",
                  value: formatPrice(stats.tipsToday),
                  sub: "Acumulado",
                  color: "text-emerald-500",
                  loading: isLoading,
                  fetching: isFetching && !isLoading,
                },
                {
                  icon: (
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                  ),
                  label: "Descuentos hoy",
                  value: formatPrice(stats.discountsToday),
                  sub: "Acumulado",
                  color: "text-muted-foreground",
                  loading: isLoading,
                  fetching: isFetching && !isLoading,
                },
                {
                  icon: <Printer className="w-4 h-4 text-primary" />,
                  label: "Impresoras",
                  value: `${stats.printersEnabled}/${stats.printersTotal}`,
                  sub: "Activas",
                  color: "text-primary",
                  loading: false,
                  fetching: false,
                },
                {
                  icon: <AlertCircle className="w-4 h-4 text-destructive" />,
                  label: "Fallos impresión",
                  value: `${stats.printFailuresToday}`,
                  sub: "Hoy",
                  color: "text-destructive",
                  loading: false,
                  fetching: false,
                },
              ].map((s) =>
                s.loading ? (
                  <Card
                    key={s.label}
                    className="bg-card/50 backdrop-blur-md border border-border/50 shadow-lg"
                  >
                    <CardHeader className="pb-1 px-4 pt-4">
                      <CardDescription className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
                        {s.icon} {s.label}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="px-4 pb-4 space-y-2 mt-1">
                      <Skeleton className="h-7 w-24 bg-muted-foreground/10" />
                      <Skeleton className="h-3 w-16 bg-muted-foreground/10" />
                    </CardContent>
                  </Card>
                ) : (
                  <Card
                    key={s.label}
                    className={cn(
                      "bg-card/50 backdrop-blur-md border-border/50 shadow-lg hover:border-primary/30 transition-all group relative",
                      s.fetching && "opacity-75"
                    )}
                  >
                    {s.fetching && (
                      <div className="absolute top-2.5 right-2.5 flex items-center justify-center">
                        <div className="w-3.5 h-3.5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                      </div>
                    )}
                    <CardHeader className="pb-1 px-4 pt-4">
                      <CardDescription className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
                        {s.icon} {s.label}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="px-4 pb-4">
                      <p className={cn("text-2xl font-black italic", s.color)}>
                        {s.value}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {s.sub}
                      </p>
                    </CardContent>
                  </Card>
                )
              )}
            </div>

            {/* ════ Platos Listos de Cocina (Hoy) ════ */}
            <div className="bg-card/40 backdrop-blur-sm rounded-2xl border border-border/50 shadow-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-border/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Utensils className="w-4 h-4 text-primary" />
                  <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Platos Listos de Cocina (Hoy)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="font-mono text-xs font-bold">
                    {effectiveDishesTotal} platos listos
                  </Badge>
                  {isApiEnabled() && (
                    <button
                      onClick={() => void refreshAdminMetrics()}
                      className="text-[10px] text-primary font-semibold hover:underline"
                    >
                      Actualizar →
                    </button>
                  )}
                </div>
              </div>
              <div className="p-4">
                {adminMetricsLoading && !adminMetricsData && effectiveDishesList.length === 0 && (
                  <p className="py-4 text-center text-xs text-muted-foreground">
                    Cargando platos salidos de cocina...
                  </p>
                )}
                {effectiveDishesList.length === 0 && (!adminMetricsLoading || adminMetricsData) && (
                  <p className="py-4 text-center text-xs text-muted-foreground">
                    Aún no han salido platos de cocina el día de hoy
                  </p>
                )}
                {effectiveDishesList.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {effectiveDishesList.map((dish, idx) => (
                      <div
                        key={`${dish.name}-${idx}`}
                        className="flex items-center justify-between p-3 rounded-xl bg-card/60 border border-border/40 hover:border-primary/30 transition-all shadow-sm"
                      >
                        <span className="text-sm font-semibold text-foreground truncate pr-2">
                          {dish.name}
                        </span>
                        <Badge variant="default" className="font-mono font-bold text-xs shrink-0">
                          {dish.quantity}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {isApiEnabled() && (
              <div className="bg-card/40 backdrop-blur-sm rounded-2xl border border-border/50 shadow-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-border/20 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Solicitudes Envío (Reservas)
                  </span>
                  <button
                    onClick={() => void refreshReservationSendRequests()}
                    className="text-[10px] text-primary font-semibold hover:underline"
                  >
                    Actualizar →
                  </button>
                </div>
                <div className="divide-y divide-border/10">
                  {reservationSendRequestsLoading && (
                    <p className="p-4 text-center text-xs text-muted-foreground">
                      Cargando...
                    </p>
                  )}
                  {!reservationSendRequestsLoading &&
                    reservationSendRequests.length === 0 && (
                      <p className="p-4 text-center text-xs text-muted-foreground">
                        Sin solicitudes pendientes
                      </p>
                    )}
                  {!reservationSendRequestsLoading &&
                    reservationSendRequests.slice(0, 20).map((req) => (
                      <div
                        key={req.id}
                        className="flex items-center gap-3 px-4 py-3"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <Badge variant="outline" className="shrink-0">
                              Mesa {req.tableNumber}
                            </Badge>
                            <span className="text-xs text-muted-foreground truncate">
                              {req.reservedAt
                                ? req.reservedAt.toLocaleString()
                                : ""}
                            </span>
                          </div>
                          <div className="text-xs text-foreground/80 truncate mt-0.5">
                            {req.reserverName} · {req.partySize} comensales ·
                            solicitó {req.requestedBy}
                          </div>
                        </div>
                        {!isReadOnly && (
                          <div className="shrink-0 flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                void rejectReservationSendRequest(req)
                              }
                            >
                              Rechazar
                            </Button>
                            <Button
                              size="sm"
                              onClick={() =>
                                void approveReservationSendRequest(req)
                              }
                            >
                              Aprobar
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {isApiEnabled() && (
              <div className="bg-card/40 backdrop-blur-sm rounded-2xl border border-border/50 shadow-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-border/20 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Tickets Fallidos (Impresión)
                  </span>
                  <div className="flex items-center gap-2">
                    <Select
                      value={String(failedStationTicketsStation)}
                      onValueChange={(v) =>
                        setFailedStationTicketsStation(v as StationKey | "all")
                      }
                    >
                      <SelectTrigger className="h-8 w-[130px] sm:w-[160px]">
                        <SelectValue placeholder="Estación" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {stationList.map((s) => (
                          <SelectItem key={s.key} value={s.key}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant="outline"
                      className="font-bold h-8"
                      onClick={() => void refreshFailedStationTickets()}
                      disabled={failedStationTicketsLoading}
                    >
                      Actualizar
                    </Button>
                  </div>
                </div>
                <div className="divide-y divide-border/10">
                  {failedStationTicketsLoading && (
                    <p className="p-4 text-center text-xs text-muted-foreground">
                      Cargando...
                    </p>
                  )}
                  {!failedStationTicketsLoading &&
                    failedStationTickets.length === 0 && (
                      <p className="p-4 text-center text-xs text-muted-foreground">
                        Sin tickets fallidos (últimas 24h)
                      </p>
                    )}
                  {!failedStationTicketsLoading &&
                    failedStationTickets.map((t) => {
                      const stationLabel =
                        stationList.find((s) => s.key === t.stationKey)
                          ?.label ?? t.stationKey;
                      const mins = Math.max(
                        0,
                        Math.floor(
                          (Date.now() - t.createdAt.getTime()) / 60_000,
                        ),
                      );
                      return (
                        <div
                          key={t.id}
                          className="flex items-center gap-3 px-4 py-3"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <Badge variant="outline" className="shrink-0">
                                Mesa {t.tableNumber}
                              </Badge>
                              <Badge variant="secondary" className="shrink-0">
                                {stationLabel}
                              </Badge>
                              <span className="text-xs text-muted-foreground truncate">
                                Ronda {t.round} · hace {mins}m
                              </span>
                            </div>
                            <div className="text-xs text-foreground/80 truncate mt-0.5">
                              {t.printError ?? "Error al imprimir"}
                            </div>
                          </div>
                          <div className="shrink-0 flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => void retryFailedStationTicket(t)}
                              disabled={failedStationTicketsLoading}
                            >
                              Reintentar
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ════ Salón — Editor visual de zonas y mesas ════ */}
        {activeTab === "salon" && (
          <div className="flex-1 flex flex-col gap-3 overflow-hidden min-h-0">
            <div className="flex items-center justify-between px-1 shrink-0">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold tracking-tight text-foreground italic">
                  Editor de Salón
                </h2>
                <Badge
                  variant="secondary"
                  className="font-bold uppercase tracking-tighter"
                >
                  {tables.length} mesas · {zoneItems.length} zonas
                </Badge>
              </div>
              <p className="text-[10px] text-muted-foreground hidden sm:block">
                Arrastra mesas para reposicionarlas · Esc cancela la herramienta
                activa
              </p>
            </div>

            <div className="flex-1 min-h-0">
              <ZoneTableEditor
                readonly={isReadOnly}
                tables={tables}
                zoneItems={zoneItems}
                zones={zones}
                onTableCreate={onTableCreate}
                onTableUpdate={onTableUpdate}
                onTableDelete={onTableDelete}
                onZoneCreate={onZoneCreate}
                onZoneUpdate={onZoneUpdate}
                onZoneDelete={onZoneDelete}
              />
            </div>
          </div>
        )}

        {activeTab === "inventory" && (<>
            <InventoryPrivate />
          </>)}

        {/* ════ Carta (Menú) ════ */}
        {activeTab === "menu" && (
          <>
            {/* <ProductsPrivate /> */}
            <div className="flex-1 flex flex-col gap-4 overflow-hidden">
              <div className="flex items-center justify-between px-1 shrink-0">
                <h2 className="text-xl font-bold tracking-tight text-foreground italic">
                  Carta de Productos
                </h2>
                {!isReadOnly && (
                  <Button
                    size="sm"
                    onClick={openNewProduct}
                    className="font-bold"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Nuevo Producto
                  </Button>
                )}
              </div>

              {/* Category pills */}
              <div className="flex gap-2 items-center overflow-x-auto no-scrollbar pb-1 shrink-0">
                <Button
                  variant={
                    selectedCategory === CATEGORY_ALL ? "default" : "outline"
                  }
                  size="sm"
                  className="font-bold text-xs shrink-0"
                  onClick={() => setSelectedCategory(CATEGORY_ALL)}
                >
                  Todas
                </Button>
                <Button
                  variant={
                    selectedCategory === CATEGORY_UNCATEGORIZED
                      ? "default"
                      : "outline"
                  }
                  size="sm"
                  className="font-bold text-xs shrink-0"
                  onClick={() => setSelectedCategory(CATEGORY_UNCATEGORIZED)}
                >
                  Sin categoría{" "}
                  {uncategorizedCount > 0 ? `(${uncategorizedCount})` : ""}
                </Button>
                {categories.map((cat) => (
                  <div
                    key={cat.id}
                    className="flex group items-center bg-card/50 border border-border/50 rounded-md"
                  >
                    <Button
                      variant={
                        selectedCategory === cat.id ? "default" : "ghost"
                      }
                      size="sm"
                      className={cn(
                        "font-bold text-xs shrink-0",
                        !isReadOnly && "rounded-r-none",
                      )}
                      onClick={() => setSelectedCategory(cat.id)}
                    >
                      {cat.name}
                    </Button>
                    {!isReadOnly && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-l-none opacity-50 group-hover:opacity-100"
                        onClick={() => openEditCategory(cat)}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
                {!isReadOnly && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="font-bold border-dashed shrink-0"
                    onClick={openNewCategory}
                  >
                    <Plus className="w-3 h-3 mr-1" /> Categoría
                  </Button>
                )}
              </div>

              <div className="flex-1 bg-card/40 backdrop-blur-sm rounded-2xl border border-border/50 overflow-hidden shadow-2xl">
                <ScrollArea className="h-full">
                  <div className="divide-y divide-border/20">
                    {filteredProducts.map((product) => (
                      <div
                        key={product.id}
                        className="flex items-center justify-between p-4 hover:bg-primary/5 transition-all"
                      >
                        <div className="flex items-center gap-4">
                          {product.image ? (
                            <div className="w-12 h-12 rounded-xl border border-border/50 overflow-hidden shrink-0">
                              <img
                                src={`${import.meta.env.VITE_API_BASE_URL?.replace("/api", "")}/storage/${product.image}`}
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center font-black text-lg text-primary border border-primary/10 shrink-0">
                              {product.name[0]}
                            </div>
                          )}
                          <div>
                            <p className="font-bold text-foreground italic">
                              {product.name}
                            </p>
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {product.description || "Sin descripción"}
                            </p>
                            <p className="text-sm font-black text-primary mt-0.5">
                              {formatPrice(product.price)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col items-end mr-2">
                            <span
                              className={cn(
                                "text-[10px] font-black uppercase mb-1",
                                product.available
                                  ? "text-emerald-500"
                                  : "text-red-500",
                              )}
                            >
                              {product.available ? "Disponible" : "Agotado"}
                            </span>
                            <Switch
                              checked={product.available}
                              disabled={isReadOnly}
                              onCheckedChange={() =>
                                !isReadOnly &&
                                toggleProductAvailability(product.id)
                              }
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 opacity-50 hover:opacity-100"
                            onClick={() =>
                              modalVRecipeRef.current?.childFunction(product)
                            }
                            title="Ver receta"
                          >
                            <ChefHat className="w-4 h-4" />
                          </Button>
                          {!isReadOnly && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 opacity-50 hover:opacity-100"
                                onClick={() => openEditProduct(product)}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>

                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 opacity-50 hover:opacity-100 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                onClick={() => handleDeleteProduct(product.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                    {filteredProducts.length === 0 && (
                      <p className="p-8 text-center text-sm text-muted-foreground">
                        Sin productos en esta categoría
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>
          </>
        )}

        {/* ════ Impresoras por Estación ════ */}
        {activeTab === "printers" && (
          <div className="flex-1 flex flex-col gap-4 overflow-hidden">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-xl font-bold tracking-tight text-foreground italic">
                Impresoras
              </h2>
              <Badge variant="outline" className="font-mono">
                {stationPrintersData.length} configs
              </Badge>
            </div>
            <div className="flex-1 overflow-y-auto no-scrollbar">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                {stationList.map((s) => {
                  const printer = getPrinter(s.key);
                  const enabled = Boolean(printer.enabled);
                  return (
                    <Card
                      key={s.key}
                      className="bg-card/40 border-border/50 hover:border-primary/30 transition-all shadow-lg"
                    >
                      <CardHeader className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <CardDescription className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-2">
                              <Printer className="w-3.5 h-3.5" /> {s.label}
                            </CardDescription>
                            <div className="mt-2 flex items-center gap-2">
                              <span
                                className={cn(
                                  "text-[10px] font-black uppercase",
                                  enabled
                                    ? "text-emerald-500"
                                    : "text-muted-foreground",
                                )}
                              >
                                {enabled ? "Habilitada" : "Deshabilitada"}
                              </span>
                              <Switch
                                checked={enabled}
                                disabled={isReadOnly}
                                onCheckedChange={(v) =>
                                  !isReadOnly &&
                                  patchPrinterLocal(s.key, { enabled: v })
                                }
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="font-bold"
                              disabled={isReadOnly || !onStationPrinterTestPrint}
                              onClick={() => void testPrintPrinter(s.key)}
                            >
                              Prueba
                            </Button>
                            <Button
                              size="sm"
                              className="font-bold"
                              disabled={isReadOnly || !onStationPrinterUpdate}
                              onClick={() => void savePrinter(s.key)}
                            >
                              Guardar
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="px-4 pb-4 space-y-3">
                        <div className="grid grid-cols-1 gap-2">
                          <div className="text-xs font-bold text-foreground">
                            Nombre
                          </div>
                          <Input
                            value={printer.name ?? ""}
                            onChange={(e) =>
                              patchPrinterLocal(s.key, { name: e.target.value })
                            }
                            placeholder="Ej: Epson TM-T20"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="grid grid-cols-1 gap-2">
                            <div className="text-xs font-bold text-foreground">
                              IP
                            </div>
                            <Input
                              value={printer.ip ?? ""}
                              onChange={(e) =>
                                patchPrinterLocal(s.key, { ip: e.target.value })
                              }
                              placeholder="Ej: 192.168.1.50"
                            />
                          </div>
                          <div className="grid grid-cols-1 gap-2">
                            <div className="text-xs font-bold text-foreground">
                              Puerto
                            </div>
                            <Input
                              inputMode="numeric"
                              value={String(printer.port ?? 9100)}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw.trim().length === 0) {
                                  patchPrinterLocal(s.key, { port: 9100 });
                                  return;
                                }
                                const n = Number(raw);
                                patchPrinterLocal(s.key, {
                                  port: Number.isFinite(n)
                                    ? Math.max(1, Math.min(65535, n))
                                    : 9100,
                                });
                              }}
                              placeholder="9100"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="grid grid-cols-1 gap-2">
                            <div className="text-xs font-bold text-foreground">
                              Formato
                            </div>
                            <select
                              className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                              value={getPrinterOptions(printer).mode}
                              onChange={(e) =>
                                patchPrinterOptions(s.key, {
                                  mode:
                                    e.target.value === "escpos"
                                      ? "escpos"
                                      : "text",
                                })
                              }
                            >
                              <option value="text">Texto</option>
                              <option value="escpos">ESC/POS</option>
                            </select>
                          </div>
                          <div className="grid grid-cols-1 gap-2">
                            <div className="text-xs font-bold text-foreground">
                              Ancho (cols)
                            </div>
                            <Input
                              inputMode="numeric"
                              value={String(getPrinterOptions(printer).cols)}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const n = Number(raw);
                                if (!Number.isFinite(n)) return;
                                patchPrinterOptions(s.key, {
                                  cols: Math.max(
                                    16,
                                    Math.min(120, Math.floor(n)),
                                  ),
                                });
                              }}
                              placeholder="57"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="grid grid-cols-1 gap-2">
                            <div className="text-xs font-bold text-foreground">
                              Papel
                            </div>
                            <select
                              className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                              value={getPrinterOptions(printer).paper}
                              onChange={(e) =>
                                patchPrinterOptions(s.key, {
                                  paper: e.target.value === "58" ? "58" : "80",
                                })
                              }
                            >
                              <option value="58">58mm</option>
                              <option value="80">80mm</option>
                            </select>
                          </div>
                          <div className="grid grid-cols-1 gap-2">
                            <div className="text-xs font-bold text-foreground">
                              Fuente
                            </div>
                            <select
                              className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                              value={getPrinterOptions(printer).font}
                              onChange={(e) =>
                                patchPrinterOptions(s.key, {
                                  font:
                                    e.target.value.toUpperCase() === "B"
                                      ? "B"
                                      : "A",
                                })
                              }
                            >
                              <option value="A">A (normal)</option>
                              <option value="B">B (condensada)</option>
                            </select>
                          </div>
                        </div>
                        <div className="flex items-center justify-between rounded-lg border border-border/50 px-3 py-2 bg-card/30">
                          <div className="text-xs font-bold text-foreground">
                            Corte de papel
                          </div>
                          <Switch
                            checked={Boolean(getPrinterOptions(printer).cut)}
                            onCheckedChange={(v) =>
                              patchPrinterOptions(s.key, { cut: v })
                            }
                          />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ════ Actividad ════ */}
        {activeTab === "activity" && (
          <>
            <ActivitiesPrivate />
            {/* <div className="flex-1 overflow-hidden flex flex-col gap-4">
              <div className="flex items-center justify-between px-1">
                <h2 className="text-xl font-bold tracking-tight text-foreground italic">
                  Registro de Actividad
                </h2>
                <Badge variant="outline" className="font-mono">
                  {activityLogs.length} eventos
                </Badge>
              </div>
              <div className="flex-1 bg-card/40 backdrop-blur-sm rounded-2xl border border-border/50 overflow-hidden shadow-2xl">
                <ScrollArea className="h-full">
                  <div className="divide-y divide-border/20">
                    {activityLogs.map((log) => (
                      <div
                        key={log.id}
                        className="p-4 hover:bg-primary/5 transition-colors"
                      >
                        <div className="flex items-center gap-3 mb-1">
                          <div
                            className={cn(
                              "w-6 h-6 rounded-lg flex items-center justify-center",
                              getActivityColor(log.action),
                            )}
                          >
                            {React.createElement(getActivityIcon(log.action), {
                              className: "w-3.5 h-3.5",
                            })}
                          </div>
                          <span className="text-xs font-bold text-foreground">
                            {getActivityLabelEs(log.action).toUpperCase()}
                          </span>
                          {log.tableNumber !== undefined && (
                            <Badge
                              variant="outline"
                              className="font-mono text-[10px]"
                            >
                              Mesa {log.tableNumber}
                            </Badge>
                          )}
                          <span className="text-[10px] text-muted-foreground ml-auto">
                            {formatTime(log.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm text-foreground/80 pl-9">
                          {formatActivityDetails(log)}
                        </p>
                        <p className="text-[10px] text-muted-foreground pl-9 italic">
                          Responsable: {log.actor} (
                          {getRoleLabelEs(log.actorRole)})
                        </p>
                      </div>
                    ))}
                    {activityLogs.length === 0 && (
                      <p className="p-8 text-center text-sm text-muted-foreground">
                        Sin actividad registrada
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div> */}
          </>
        )}

        {/* ════ Caja (Historial) ════ */}
        {activeTab === "cashier" && (
          <>
            <PaymentsPrivate />
          </>
        )}

        {/* ════ Staff ════ */}
        {activeTab === "users" && (
          <>
            <UsersPrivate />
          </>
        )}

        {/* ════ Clientes ════ */}
        {activeTab === "clients" && (
          <>
            <ClientsPrivate />
          </>
        )}
      </div>

      {/* Modals de CRUD */}

      <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? "Editar Categoría" : "Nueva Categoría"}
            </DialogTitle>
            <DialogDescription>
              Agrupa los productos de la carta asociándolos a esta categoría.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="catName" className="text-sm font-bold">
                Nombre
              </label>
              <Input
                id="catName"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Ej: Bebidas Frías"
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="catStation" className="text-sm font-bold">
                Estación (opcional, por defecto hereda Cocina)
              </label>
              <select
                id="catStation"
                value={categoryStationKey || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setCategoryStationKey(
                    val === "__none__" ? "" : (val as StationKey),
                  );
                }}
                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="__none__">
                  Sin estación (Cocina por defecto)
                </option>
                <option value="cocina">Cocina</option>
                <option value="bar">Bar</option>
                <option value="retiro">Retiro</option>
                <option value="delivery">Delivery</option>
              </select>
            </div>
          </div>
          <DialogFooter className="flex justify-between items-center w-full">
            {editingCategory ? (
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleDeleteCategory(editingCategory.id)}
              >
                Eliminar
              </Button>
            ) : (
              <div></div>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsCategoryModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button onClick={handleSaveCategory}>Guardar</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isCustomerModalOpen} onOpenChange={setIsCustomerModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            {/*  <DialogTitle>
              {editingCustomer ? "Editar Cliente" : "Nuevo Cliente"}
            </DialogTitle> */}
            <DialogDescription>
              Registra personas o empresas para asociar pagos y aplicar
              descuentos o crédito.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="customerType" className="text-sm font-bold">
                Tipo
              </label>
              <select
                id="customerType"
                value={customerType}
                onChange={(e) =>
                  setCustomerType(e.target.value as Customer["type"])
                }
                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="person">Persona</option>
                <option value="company">Empresa</option>
              </select>
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-bold">Nombre</label>
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Ej: Cliente S.A."
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="grid gap-2">
                <label className="text-sm font-bold">Email</label>
                <Input
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="ej: cliente@empresa"
                />
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-bold">Teléfono</label>
                <Input
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="ej: +56 9 5555 5555"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="grid gap-2">
                <label className="text-sm font-bold">
                  {customerType === "person" ? "RUN" : "RUT"}
                </label>
                <Input
                  value={customerTaxId}
                  onChange={(e) => setCustomerTaxId(e.target.value)}
                  placeholder="ej: 76.123.456-7"
                />
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-bold">Descuento (%)</label>
                <Input
                  inputMode="numeric"
                  value={String(parseDigitsToInt(customerDiscount))}
                  onChange={(e) =>
                    setCustomerDiscount(e.target.value.replace(/[^\d]/g, ""))
                  }
                  placeholder="0"
                />
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-bold">Límite crédito</label>
                <Input
                  inputMode="numeric"
                  value={formatInputThousands(customerCreditLimit)}
                  onChange={(e) =>
                    setCustomerCreditLimit(e.target.value.replace(/[^\d]/g, ""))
                  }
                  placeholder="0"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Switch
                checked={customerCreditEnabled}
                onCheckedChange={setCustomerCreditEnabled}
              />
              <span className="text-sm">Habilitar crédito</span>
            </div>
          </div>
          <DialogFooter className="flex justify-between items-center w-full">
            {/*  {editingCustomer ? (
              <Button
                type="button"
                variant="destructive"
                onClick={() =>
                  editingCustomer && deleteCustomer(editingCustomer)
                }
                disabled={!onCustomerDelete}
              >
                Eliminar
              </Button>
            ) : (
              <span />
            )} */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCustomerModalOpen(false)}
              >
                Cancelar
              </Button>
              {/*  <Button
                type="button"
                onClick={() => void saveCustomer()}
                disabled={
                  (editingCustomer ? !onCustomerUpdate : !onCustomerCreate) ||
                  customerName.trim().length === 0
                }
              >
                Guardar
              </Button> */}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Detalle de Pago</DialogTitle>
            <DialogDescription>
              {selectedPayment
                ? `${selectedPayment.number_ticket ? "Ticket #" + selectedPayment.number_ticket + " · " : null}  Mesa ${selectedPayment.tableNumber} · ${selectedPayment.method.toUpperCase()}`
                : "Cargando..."}
            </DialogDescription>
          </DialogHeader>
          {!selectedPayment ? (
            <div className="py-6 text-sm text-muted-foreground">
              Cargando...
            </div>
          ) : (
            <div className="grid gap-3">
              {selectedPayment.customerId ? (
                <div className="text-sm">
                  Cliente:{" "}
                  <span className="font-semibold">
                    {customersData.find(
                      (c) => c.id === selectedPayment.customerId,
                    )?.name ?? selectedPayment.customerId}
                  </span>
                </div>
              ) : null}
              <div className="text-xs text-muted-foreground">
                {selectedPayment.paidAt
                  ? selectedPayment.paidAt.toLocaleString("es-CL")
                  : "Sin fecha"}
              </div>
              <div className="rounded-lg border border-border/50 overflow-hidden">
                <div className="px-3 py-2 bg-muted/30 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Ítems
                </div>
                <div className="divide-y divide-border/20">
                  {(selectedPayment.receipt?.items ?? []).length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground">
                      Sin detalle
                    </div>
                  ) : (
                    (selectedPayment.receipt?.items ?? []).map((it, idx) => (
                      <div
                        key={`${selectedPayment.id}-${idx}`}
                        className="p-3 flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">
                            {it.quantity}x {it.productName}
                          </div>
                          {it.notes ? (
                            <div className="text-xs text-muted-foreground">
                              Nota: {it.notes}
                            </div>
                          ) : null}
                        </div>
                        <div className="text-sm font-semibold text-foreground whitespace-nowrap">
                          {formatPrice(it.quantity * it.unitPrice)}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-muted-foreground">Subtotal</div>
                <div className="text-right font-semibold">
                  {formatPrice(selectedPayment.subtotal)}
                </div>
                <div className="text-muted-foreground">Descuento</div>
                <div className="text-right font-semibold">
                  -{formatPrice(selectedPayment.discount)}
                </div>
                <div className="text-muted-foreground">IVA</div>
                <div className="text-right font-semibold">
                  {formatPrice(selectedPayment.tax)}
                </div>
                <div className="text-muted-foreground">Propina</div>
                <div className="text-right font-semibold">
                  {formatPrice(selectedPayment.tip)}
                </div>
                <div className="font-bold">TOTAL</div>
                <div className="text-right font-black">
                  {formatPrice(selectedPayment.total)}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsPaymentModalOpen(false)}
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isProductModalOpen} onOpenChange={setIsProductModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? "Editar Producto" : "Nuevo Producto"}
            </DialogTitle>
            <DialogDescription>
              Añade un artículo a la carta. La imagen se optimizará
              automáticamente (formato recomendado: PNG, JPG, WEBP).
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="flex gap-4">
              <div className="flex-1 grid gap-2">
                <label className="text-sm font-bold">Imagen</label>
                <div className="relative w-full h-32 rounded-xl border-2 border-dashed border-border/50 hover:bg-muted/50 transition-colors flex items-center justify-center overflow-hidden">
                  {productImagePreview ? (
                    <img
                      src={productImagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center text-muted-foreground p-4 text-center">
                      <ImageIcon className="w-8 h-8 opacity-50 mb-2" />
                      <span className="text-xs font-medium">
                        Click para seleccionar
                      </span>
                    </div>
                  )}
                  <input
                    type="file"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    accept="image/png, image/jpeg, image/webp"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setProductImage(file);
                        setProductImagePreview(URL.createObjectURL(file));
                      }
                    }}
                  />
                </div>
              </div>
              <div className="flex-[2] grid gap-4">
                <div className="grid gap-2">
                  <label htmlFor="prodName" className="text-sm font-bold">
                    Nombre
                  </label>
                  <Input
                    id="prodName"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="Ej: Pisco Sour"
                  />
                </div>
                <div className="grid gap-2">
                  <label htmlFor="prodPrice" className="text-sm font-bold">
                    Precio ($)
                  </label>
                  <Input
                    id="prodPrice"
                    type="number"
                    min="0"
                    step="50"
                    value={productPrice}
                    onChange={(e) => setProductPrice(e.target.value)}
                    placeholder="Ej: 5500"
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-2">
              <label htmlFor="prodCat" className="text-sm font-bold">
                Categoría
              </label>
              <select
                id="prodCat"
                value={productCategoryId}
                onChange={(e) => setProductCategoryId(e.target.value)}
                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value={CATEGORY_UNCATEGORIZED}>Sin categoría</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-2">
              <label htmlFor="prodDesc" className="text-sm font-bold">
                Descripción (Opcional)
              </label>
              <Input
                id="prodDesc"
                value={productDesc}
                onChange={(e) => setProductDesc(e.target.value)}
                placeholder="Detalles de preparación o ingredientes..."
              />
            </div>

            <div className="grid gap-2">
              <label htmlFor="prodStation" className="text-sm font-bold">
                Estación (opcional; si no se define, usa la de la categoría o
                Cocina)
              </label>
              <select
                id="prodStation"
                value={productStationKey || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setProductStationKey(
                    val === "__none__" ? "" : (val as StationKey),
                  );
                }}
                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="__none__">
                  Sin estación (hereda categoría/Cocina)
                </option>
                <option value="cocina">Cocina</option>
                <option value="bar">Bar</option>
                <option value="retiro">Retiro</option>
                <option value="delivery">Delivery</option>
              </select>
            </div>

            <div className="grid gap-3 border border-border/40 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">
                  Disponibilidad por días/horarios
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setAvailabilityRules((prev) => [
                      ...prev,
                      { days: [], start: "12:00", end: "23:59" },
                    ])
                  }
                >
                  + Regla
                </Button>
              </div>
              {availabilityRules.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Sin reglas: usa el switch “Disponible” general.
                </p>
              )}
              {availabilityRules.map((r, idx) => (
                <div
                  key={idx}
                  className="flex flex-col gap-2 p-2 rounded-lg bg-card/60 border border-border/40"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    {["D", "L", "M", "M", "J", "V", "S"].map((label, dIdx) => {
                      const dayMap = [0, 1, 2, 3, 4, 5, 6];
                      const day = dayMap[dIdx];
                      const selected = r.days.includes(day);
                      return (
                        <button
                          key={dIdx}
                          type="button"
                          onClick={() =>
                            setAvailabilityRules((prev) =>
                              prev.map((rr, i) =>
                                i === idx
                                  ? {
                                      ...rr,
                                      days: selected
                                        ? rr.days.filter((d) => d !== day)
                                        : [...rr.days, day],
                                    }
                                  : rr,
                              ),
                            )
                          }
                          className={`px-2 py-1 rounded-md text-xs font-bold border ${selected ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground border-border/50"}`}
                          title={
                            ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"][
                              dIdx
                            ]
                          }
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        Desde
                      </span>
                      <Input
                        type="time"
                        value={r.start}
                        onChange={(e) =>
                          setAvailabilityRules((prev) =>
                            prev.map((rr, i) =>
                              i === idx ? { ...rr, start: e.target.value } : rr,
                            ),
                          )
                        }
                        className="h-8 w-28"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        Hasta
                      </span>
                      <Input
                        type="time"
                        value={r.end}
                        onChange={(e) =>
                          setAvailabilityRules((prev) =>
                            prev.map((rr, i) =>
                              i === idx ? { ...rr, end: e.target.value } : rr,
                            ),
                          )
                        }
                        className="h-8 w-28"
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 ml-auto"
                      onClick={() =>
                        setAvailabilityRules((prev) =>
                          prev.filter((_, i) => i !== idx),
                        )
                      }
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid gap-3 border border-border/40 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">
                  Precios especiales por día/horario
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setPriceRules((prev) => [
                      ...prev,
                      {
                        days: [],
                        start: "12:00",
                        end: "23:59",
                        price: Number(productPrice || 0),
                      },
                    ])
                  }
                >
                  + Regla
                </Button>
              </div>
              {priceRules.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Sin reglas: usa el precio base.
                </p>
              )}
              {priceRules.map((r, idx) => (
                <div
                  key={idx}
                  className="flex flex-col gap-2 p-2 rounded-lg bg-card/60 border border-border/40"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    {["D", "L", "M", "M", "J", "V", "S"].map((label, dIdx) => {
                      const dayMap = [0, 1, 2, 3, 4, 5, 6];
                      const day = dayMap[dIdx];
                      const selected = r.days.includes(day);
                      return (
                        <button
                          key={dIdx}
                          type="button"
                          onClick={() =>
                            setPriceRules((prev) =>
                              prev.map((rr, i) =>
                                i === idx
                                  ? {
                                      ...rr,
                                      days: selected
                                        ? rr.days.filter((d) => d !== day)
                                        : [...rr.days, day],
                                    }
                                  : rr,
                              ),
                            )
                          }
                          className={`px-2 py-1 rounded-md text-xs font-bold border ${selected ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground border-border/50"}`}
                          title={
                            ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"][
                              dIdx
                            ]
                          }
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        Desde
                      </span>
                      <Input
                        type="time"
                        value={r.start}
                        onChange={(e) =>
                          setPriceRules((prev) =>
                            prev.map((rr, i) =>
                              i === idx ? { ...rr, start: e.target.value } : rr,
                            ),
                          )
                        }
                        className="h-8 w-28"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        Hasta
                      </span>
                      <Input
                        type="time"
                        value={r.end}
                        onChange={(e) =>
                          setPriceRules((prev) =>
                            prev.map((rr, i) =>
                              i === idx ? { ...rr, end: e.target.value } : rr,
                            ),
                          )
                        }
                        className="h-8 w-28"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        Precio
                      </span>
                      <Input
                        type="number"
                        min="0"
                        step="50"
                        value={r.price.toString()}
                        onChange={(e) =>
                          setPriceRules((prev) =>
                            prev.map((rr, i) =>
                              i === idx
                                ? { ...rr, price: Number(e.target.value || 0) }
                                : rr,
                            ),
                          )
                        }
                        className="h-8 w-28"
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 ml-auto"
                      onClick={() =>
                        setPriceRules((prev) =>
                          prev.filter((_, i) => i !== idx),
                        )
                      }
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="flex justify-between w-full">
            <Button
              variant="outline"
              onClick={() => setIsProductModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button onClick={handleSaveProduct}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ModalVRecipe ref={modalVRecipeRef} isReadOnly={isReadOnly} />
    </div>
  );
}

